import { ChipGroup } from "../../shared/ui/ChipGroup";
import { DateField } from "../../shared/ui/DateField";
import type { BudgetRecurrence } from "../../entities/ledger/types";

export interface BudgetScheduleForm {
  repeating: boolean;
  startDate: string;
  endDate: string;
  frequency: BudgetRecurrence["frequency"];
  interval: string;
  timeZone: string;
  ending: "never" | "date" | "count";
  untilDate: string;
  count: string;
}

export function initialBudgetSchedule(start: string, end: string): BudgetScheduleForm {
  return {
    repeating: false, startDate: start.slice(0, 10),
    endDate: new Date(new Date(end).getTime() - 1).toISOString().slice(0, 10),
    frequency: "month", interval: "1", timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ending: "never", untilDate: "", count: "12",
  };
}

export function validateBudgetSchedule(value: BudgetScheduleForm): string | undefined {
  if (!value.startDate) return "Choose a start date.";
  try { new Intl.DateTimeFormat("en", { timeZone: value.timeZone }).format(); }
  catch { return "Choose a valid IANA time zone, such as Europe/Kyiv."; }
  if (!value.repeating) return !value.endDate || value.endDate < value.startDate ? "The end date must be on or after the start date." : undefined;
  if (!/^\d+$/.test(value.interval) || Number(value.interval) < 1) return "Repeat every must be a positive whole number.";
  if (value.ending === "count" && (!/^\d+$/.test(value.count) || Number(value.count) < 1)) return "Choose a positive whole number of periods.";
  if (value.ending === "date" && (!value.untilDate || value.untilDate < value.startDate)) return "The last start date must be on or after the first start date.";
}

export function BudgetRecurrenceFields({ value, onChange }: { value: BudgetScheduleForm; onChange: (value: BudgetScheduleForm) => void }) {
  const set = (patch: Partial<BudgetScheduleForm>) => onChange({ ...value, ...patch });
  return <fieldset className="stacked-form">
    <legend>Budget period</legend>
    <ChipGroup label="Budget repetition" value={value.repeating ? "repeat" : "once"}
      options={[{ value: "once", label: "One-off" }, { value: "repeat", label: "Repeats" }]}
      onChange={(choice) => set({ repeating: choice === "repeat" })} />
    <div className="field-grid">
      <DateField label="Starts on" value={value.startDate} onChange={(startDate) => set({ startDate })} required />
      {!value.repeating ? <DateField label="Ends on (included)" value={value.endDate} min={value.startDate} onChange={(endDate) => set({ endDate })} required /> : null}
      <label>Time zone<input value={value.timeZone} onChange={(event) => set({ timeZone: event.target.value })} placeholder="Europe/Kyiv" required /></label>
    </div>
    {value.repeating ? <>
      <label>Repeat every<input type="number" min="1" step="1" value={value.interval} onChange={(event) => set({ interval: event.target.value })} required /></label>
      <ChipGroup label="Repeat unit" value={value.frequency} options={[
        { value: "day", label: "Days" }, { value: "week", label: "Weeks" },
        { value: "month", label: "Months" }, { value: "year", label: "Years" },
      ]} onChange={(frequency) => set({ frequency: frequency as BudgetRecurrence["frequency"] })} />
      <ChipGroup label="Repeating budget ends" value={value.ending} options={[
        { value: "never", label: "Never" }, { value: "date", label: "On a date" }, { value: "count", label: "After N periods" },
      ]} onChange={(ending) => set({ ending: ending as BudgetScheduleForm["ending"] })} />
      {value.ending === "date" ? <DateField label="Last allowed period start (included)" min={value.startDate} value={value.untilDate} onChange={(untilDate) => set({ untilDate })} required /> : null}
      {value.ending === "count" ? <label>Number of periods<input type="number" min="1" step="1" value={value.count} onChange={(event) => set({ count: event.target.value })} required /></label> : null}
      <p className="panel-note">The full limit renews every {value.interval || "…"} {value.frequency}(s), starting {value.startDate}. Each period has its own spending and remaining amount. Unspent money does not carry forward.</p>
    </> : null}
  </fieldset>;
}
